import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { POST_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { BulkActionResponseData } from '@utils/types/back-office/priority-queue';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type CleanupParams = {
  max_items?: number;
  older_than_hours?: number;
};

export const useBulkCleanupCompleted = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const bulkCleanupCompleted = async (params?: CleanupParams) => {
    const payload = {
      max_items: params?.max_items ?? 1000,
      older_than_hours: params?.older_than_hours ?? 24
    };

    const response = await Axios.post<BulkActionResponseData>(
      POST_URL().bulkCleanupCompleted,
      payload
    );
    return response.data;
  };

  return useMutation<
    BulkActionResponseData,
    AxiosError<BaseError>,
    CleanupParams | undefined
  >(bulkCleanupCompleted, {
    onSuccess: data => {
      const count = data.result?.affected_count || 0;
      toast.success(`${t('cleaned-up-completed-items')}: ${count}`);
      queryClient.invalidateQueries(['priorityQueue']);
      queryClient.invalidateQueries(['queueSummary']);
    },
    onError: (error: AxiosError<BaseError>) => {
      const errorMessage =
        error.response?.data?.error_description ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        t('failed-to-cleanup-completed-items');
      toast.error(errorMessage);
    }
  });
};
